/**
 * HostelRoomsSection Component
 * Main component for displaying hostel rooms
 * Integrates with the hostel room APIs
 */

import { useState } from 'react';
import { Loader, AlertCircle } from 'lucide-react';
import { useHostelRooms } from '../../hooks/useHostelRooms.js';
import RoomCard from './RoomCard.jsx';
import RoomLayoutModal from './RoomLayoutModal.jsx';

const HostelRoomsSection = ({ hostelId, hostelName = 'Hostel' }) => {
  const { rooms, loading, error, selectedRoom, setSelectedRoom, roomLayout, layoutLoading } =
    useHostelRooms(hostelId);
  const [showModal, setShowModal] = useState(false);

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRoom(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader className="w-6 h-6 text-green-400 animate-spin" />
          <span className="text-gray-300">Loading rooms...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg border border-red-700 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-400 font-semibold">Failed to Load Rooms</h3>
            <p className="text-gray-300 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (rooms.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
        <p className="text-gray-400">No rooms available at this hostel</p>
      </div>
    );
  }

  // Available rooms count
  const availableCount = rooms.filter((r) => r.available).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Available Rooms</h2>
        <p className="text-gray-400">
          {availableCount} of {rooms.length} rooms available
        </p>
      </div>

      {/* Rooms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rooms.map((room) => (
          <RoomCard
            key={room.id}
            room={room}
            isSelected={selectedRoom?.id === room.id}
            onClick={() => handleRoomClick(room)}
          />
        ))}
      </div>

      {/* Room Layout Modal */}
      {showModal && (
        <RoomLayoutModal
          room={selectedRoom}
          layout={roomLayout}
          loading={layoutLoading}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default HostelRoomsSection;
